import React, { useCallback, useMemo, useRef, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { BreadcrumbView } from '../../context/breadcrumb.context'
import { IconEmail, IconNotification } from '../icon/icon'
import { useLoad } from '../hooks/load'
import { FleetPage } from '../sso-workspace/fleet-page'
import { FleetGrid } from '../sso-workspace/fleet-grid'
import { Pill } from '../sso-workspace/pill'
import { formatAgo, formatDateTime, serverMessage } from '../sso-workspace/format'
import { TableAgInfinite } from '../table-builder/table-ag-infinite'
import { ColumnFormat, ColumnMenu, ColumnText } from '../table-builder/table-columns'
import { getQueryKeys } from '../table-builder/hooks'
import { Menu, MenuDetails, MenuItem } from '../menu/menu'
import { OrgPicker } from '../org-search/org-picker'
import { contentGet } from './content-api'
import { EmailPanel } from './message-detail'
import { scopeOf } from './scope'
import { statusLabel, statusTone, templateLabel } from './labels'
import { ErrorReference } from '../error/error-reference'
import './email-activity.scss'
import { notifyAvailable } from '../../api-new/state/notify'
import { timelinePath } from '../../layout/authorized/admin/notify/paths'
import { SECTION } from '../navigation/sections'

// The server's largest page (rapid-api-content email-activity MaxLimit),
// which is also the grid's block size (TableAgInfinite cacheBlockSize).
export const PAGE_SIZE = 200

// The organization filter's default: every organization.
const ALL_ORGANIZATIONS = { value: '', label: 'Every organization' }

const NO_FILTERS = { organizationID: '', templateKey: '', status: '', problems: false, from: '', to: '' }

// A yyyy-mm-dd from a date input, as the start of that day here.
const dayStart = value => {
  if (!value) return ''
  const [y, m, d] = value.split('-').map(Number)
  return new Date(y, m - 1, d).toISOString()
}

// "To" includes the whole day: the next day's start.
const dayAfter = value => {
  if (!value) return ''
  const [y, m, d] = value.split('-').map(Number)
  return new Date(y, m - 1, d + 1).toISOString()
}

// The query for one page of messages.
export const messageParams = ({ filters, system, recipient, limit, before }) => ({
  scope: scopeOf(system),
  limit,
  recipient: recipient || '',
  organizationID: system ? filters.organizationID : '',
  templateKey: filters.templateKey,
  status: filters.problems ? '' : filters.status,
  problems: filters.problems ? 'true' : '',
  since: dayStart(filters.from),
  until: dayAfter(filters.to),
  before
})

// The grid's datasource for one set of filters. The endpoint pages by keyset
// cursor, newest first (`before` = the last id of the previous page), while
// the infinite row model asks for rows by offset. Each block's cursor is the
// last id of the block before it, recorded as blocks arrive; the row model
// only ever asks for the block after one it already has (or one it had and
// dropped from its cache), so a cursor is always known. The row count stays
// open (-1) until a short page says where the list ends.
export const makeGetRows = ({ load, isCurrent }) => {
  const cursors = new Map()

  return async (start, end, search) => {
    const limit = Math.min(end - start, PAGE_SIZE)
    const before = start === 0 ? undefined : cursors.get(start)
    if (start > 0 && before === undefined) return { data: [], count: start }

    const page = (await load({ limit, before, recipient: search })) || []
    if (!isCurrent()) return undefined

    if (page.length) cursors.set(start + page.length, page[page.length - 1].id)
    return { data: page, count: page.length < limit ? start + page.length : -1 }
  }
}

// The query parameter that names the open email: ?message=<id>. A query
// parameter, not a path segment: the page's BreadcrumbView draws the page
// only on its own exact path, and the list must stay drawn under the panel.
export const MESSAGE_PARAM = 'message'

// The email the URL names, or null.
export const messageOf = search => {
  const v = new URLSearchParams(search || '').get(MESSAGE_PARAM)
  return v && /^[1-9]\d{0,9}$/.test(v) ? Number(v) : null
}

// search with the open email set (id) or removed (null), every other
// parameter (the grid's search and scroll position) kept.
export const searchWithMessage = (search, id) => {
  const params = new URLSearchParams(search || '')
  if (id) params.set(MESSAGE_PARAM, String(id))
  else params.delete(MESSAGE_PARAM)
  const out = `${params}`
  return out ? `?${out}` : ''
}

// Email activity (spec 2026-09-27-email-activity-design): every email the
// platform sends and what happened to it. The same page serves the staff
// Internal tools (`system`: every organization, an Organization column and
// filter) and the organization's own page (its own email only, for super
// admins too). The page names its view in every request; the server
// enforces it.
//
// The list is the table-builder's server-paged grid (TableAgInfinite): its
// search box finds recipients, and scrolling loads older email. The other
// filters sit above it; changing one starts a new grid for the new filters.
//
// Opening an email (its row, or its details button) opens its panel over the
// list (owner 2026-10-07, like the audit History drawer): the URL names it
// (?message=<id>), so a link to it works and Back closes it. The list stays
// mounted underneath, so closing returns to it exactly as it was.
export const EmailActivityPage = ({ system: systemPage = false }) => {
  const history = useHistory()
  const location = useLocation()
  const openID = messageOf(location.search)

  const [meta, setMeta] = useState(undefined)
  const [filters, setFilters] = useState(NO_FILTERS)
  // Bumped by "Clear filters", so a grid whose only filter was its search
  // starts again too.
  const [generation, setGeneration] = useState(0)
  const [error, setError] = useState(null)
  const [cause, setCause] = useState(null)

  const tableRef = useRef()

  // The organization's page never shows other organizations, whatever the
  // server says; the Internal tools page shows them once the server agrees.
  const system = Boolean(systemPage && meta && meta.scope === 'system')
  // The page's crumb is its sidebar entry's: Internal tools or the organization's.
  const section = systemPage ? SECTION.staffEmailActivity : SECTION.emailActivity

  const rangeError = filters.from && filters.to && filters.from > filters.to ? 'From must be on or before To.' : null

  useLoad(async () => {
    try {
      const m = await contentGet('email-activity/meta', { scope: scopeOf(systemPage) })
      setMeta(m || null)
    } catch (err) {
      setMeta(null)
      setError(serverMessage(err, "Email activity couldn't be loaded."))
      setCause(err)
    }
  }, [])

  const gridKey = `${generation}:${JSON.stringify(filters)}`

  // A response for filters the page has moved on from never lands (review
  // I-5): each grid's datasource checks it still belongs to the current key.
  const currentKey = useRef(gridKey)
  currentKey.current = gridKey

  const getRows = useMemo(
    () => {
      const mine = gridKey
      const fetchRows = makeGetRows({
        isCurrent: () => currentKey.current === mine,
        load: ({ limit, before, recipient }) =>
          contentGet('email-activity/message', messageParams({ filters, system, recipient, limit, before }))
      })

      return async (start, end, search) => {
        // An impossible range asks the server nothing (re-review N-5).
        if (rangeError) return { data: [], count: 0 }
        try {
          setError(null)
          setCause(null)
          return await fetchRows(start, end, search)
        } catch (err) {
          if (currentKey.current !== mine) return undefined
          setError(serverMessage(err, start === 0 ? "Email activity couldn't be loaded." : "Older email couldn't be loaded."))
          setCause(err)
          return { data: [], count: start }
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [gridKey, system, rangeError]
  )

  const set = (key, value) => setFilters(f => ({ ...f, [key]: value }))

  // Open: a new history entry, so Back closes the panel. Close: back to the
  // entry that opened it, else (a link straight to the email) to the list.
  const openEmail = useCallback(row => {
    if (!row || !row.id) return
    const here = history.location
    // Already open (a second click, a double click): one history entry,
    // so one Back closes it.
    if (messageOf(here.search) === row.id) return
    history.push({ pathname: here.pathname, search: searchWithMessage(here.search, row.id), state: { emailActivityList: true } })
  }, [history])
  const closeEmail = () => {
    if (location.state && location.state.emailActivityList) history.goBack()
    else history.replace({ pathname: location.pathname, search: searchWithMessage(location.search, null) })
  }

  // Filters other than Problems (which has its own empty sentence). The
  // grid's own search reports itself (its "no matches" state).
  const filtered = Object.entries(filters).some(([k, v]) => k !== 'problems' && Boolean(v))

  const clear = () => {
    // The grid keeps its search in the query string; drop it with the rest.
    const keys = getQueryKeys()
    const params = new URLSearchParams(history.location.search)
    params.delete(keys.search)
    params.delete(keys.index)
    history.replace({ search: `${params}` })
    setFilters(NO_FILTERS)
    setGeneration(g => g + 1)
  }

  const statuses = (meta && meta.statuses) || []
  const templateKeys = (meta && meta.templateKeys) || []

  const purpose = system
    ? 'Every email EducationXR sends and what happened to it, newest first. Open one to see what happened to it.'
    : 'Email EducationXR sent for your organization and what happened to it, newest first. Open one to see what happened to it.'

  const emptyState = {
    Icon: IconEmail,
    title: rangeError ? 'Check the dates' : filtered ? 'No matches' : filters.problems ? 'No problems' : 'No email yet',
    message: rangeError
      ? 'Fix the dates to see email.'
      : filtered
      ? 'No email matches these filters.'
      : filters.problems ? 'No problems. Nothing bounced, was dropped, was marked as spam or failed to send.' : 'No email has been sent yet. Invitations, password resets and alerts appear here as they go out.',
    action: { label: 'Clear filters', onClick: clear, show: Boolean(filtered || filters.problems) }
  }

  // The grid's search is on: "Clear filters" clears it with every other
  // filter (the grid's own action would clear only the search).
  const noMatchState = { Icon: IconEmail, title: 'No matches', message: 'No email matches these filters.', action: { label: 'Clear filters', onClick: clear } }

  return (
    <BreadcrumbView Icon={section.Icon} text={section.text}>
      <FleetPage
        title="Email activity"
        purpose={purpose}
        actions={
          <button type="button" className="btn btn-sm sw-btn" aria-pressed={filters.problems ? 'true' : 'false'} onClick={() => set('problems', !filters.problems)}>
            Problems
          </button>
        }>
        {error && <p className="text-danger small" role="alert">{error}</p>}
        {error && <ErrorReference error={cause} />}
        {rangeError && <p className="text-danger small" role="alert">{rangeError}</p>}
        {meta && meta.available === false &&
          <p className="sw-hint">This environment isn't recording email yet. Email sent before it was set up doesn't appear here.</p>}

        {meta &&
          <>
            <div className="sw-toolbar ea-toolbar">
              {system &&
                <span className="ea-filters-org">
                  <OrgPicker id="ea-org" label="Organization" value={filters.organizationID} onChange={v => set('organizationID', v)} allOption={ALL_ORGANIZATIONS} small />
                </span>}
              <select className="form-select form-select-sm" aria-label="Email type" value={filters.templateKey} onChange={e => set('templateKey', e.target.value)}>
                <option value="">Every email type</option>
                {templateKeys.map(k => <option key={k} value={k}>{templateLabel(k)}</option>)}
              </select>
              <select className="form-select form-select-sm" aria-label="Status" value={filters.status} disabled={filters.problems} onChange={e => set('status', e.target.value)}>
                <option value="">Every status</option>
                {statuses.map(s => <option key={s} value={s}>{statusLabel(s)}</option>)}
              </select>
              <span className="ea-filters-date">
                <label htmlFor="ea-from">From</label>
                <input id="ea-from" type="date" className="form-control form-control-sm" value={filters.from} onChange={e => set('from', e.target.value)} />
              </span>
              <span className="ea-filters-date">
                <label htmlFor="ea-to">To</label>
                <input id="ea-to" type="date" className="form-control form-control-sm" value={filters.to} onChange={e => set('to', e.target.value)} />
              </span>
            </div>

            <FleetGrid title="email" tableRef={tableRef} exportName="email-activity">
              <TableAgInfinite
                key={gridKey}
                ref={tableRef}
                onGetRows={getRows}
                onRowClicked={openEmail}
                emptyState={emptyState}
                noMatchState={noMatchState}>
                {/* Each column is as wide as its content (the table builder's
                    default), so a status's two pills never clip; the email's
                    name and key are structured and are never cut off. */}
                <ColumnFormat field="createdAt" header="When" render={agoCell} sortable={false} />
                <ColumnText field="recipient" header="Recipient" sortable={false} />
                <ColumnFormat field="templateKey" header="Email" render={templateCell} sortable={false} fit="content" />
                {system && <ColumnFormat field="organizationName" header="Organization" render={organizationCell} sortable={false} />}
                <ColumnFormat field="status" header="Status" render={statusCell} sortable={false} />
                <ColumnMenu
                  menu={m =>
                    <Menu small>
                      <MenuDetails text="details" pin show onClick={() => openEmail(m)} />
                      <MenuItem Icon={IconNotification} text="timeline" pin show={Boolean(m.notificationId) && notifyAvailable()} onClick={() => history.push(timelinePath(m.notificationId, system))} />
                    </Menu>}
                />
              </TableAgInfinite>
            </FleetGrid>

            {openID && <EmailPanel id={openID} system={system} onClose={closeEmail} />}
          </>}
      </FleetPage>
    </BreadcrumbView>
  )
}

const agoCell = row => (row.value ? <span title={formatDateTime(row.value)}>{formatAgo(row.value)}</span> : '')

const templateCell = row => (row.data ? <span>{templateLabel(row.value)} <span className="sw-hint sw-mono small">{row.value}</span></span> : '')

const organizationCell = row => (row.data ? row.value || <span className="sw-hint">None</span> : '')

const statusCell = row =>
  row.data
    ? <span className="d-inline-flex flex-nowrap gap-1">
        <Pill tone={statusTone(row.value)} title={formatDateTime(row.data.statusAt)}>{statusLabel(row.value)}</Pill>
        {row.data.caught && <Pill tone="info" title="Kept by the dev catcher, not sent">Caught</Pill>}
      </span>
    : ''
